class NewsFilter {
  constructor(selector = ".js-news-filter") {
    this.root = document.querySelector(selector);

    if (!this.root) return;

    this.results = this.root.querySelector(".js-news-filter-results");

    this.termButtons = Array.from(
      this.root.querySelectorAll(".js-news-filter-term"),
    );

    /**
     * ページャークリック後のスクロール先
     */
    this.scrollTarget = document.querySelector("#js-news-archive-cnt");

    this.ajaxUrl = this.root.dataset.ajaxUrl || "";

    this.nonce = this.root.dataset.nonce || "";

    this.archiveUrl = this.root.dataset.archiveUrl || window.location.href;

    this.currentTerm = this.root.dataset.currentTerm || "";

    this.currentPage = Number(this.root.dataset.currentPage || 1);

    this.isLoading = false;

    /**
     * AJAXで生成された.js-inviewを
     * 再監視するためのIntersectionObserver
     */
    this.inviewObserver = null;

    this.handleTermClick = this.handleTermClick.bind(this);

    this.handleResultsClick = this.handleResultsClick.bind(this);

    this.handlePopState = this.handlePopState.bind(this);

    this.init();
  }

  /**
   * 初期化
   */
  init() {
    if (!this.results || !this.ajaxUrl || !this.nonce) {
      return;
    }

    /**
     * NEWS一覧用のInView監視を作成
     */
    this.createInviewObserver();

    /**
     * 初期表示の記事も監視対象へ登録
     */
    this.observeInviewItems();

    this.bindEvents();

    this.updateActiveTerm(this.currentTerm);
  }

  /**
   * イベント登録
   */
  bindEvents() {
    this.termButtons.forEach((button) => {
      button.addEventListener("click", this.handleTermClick);
    });

    /**
     * AJAX後にWP-PageNavi自体が差し替わるため、
     * resultsへイベントデリゲーション
     */
    this.results.addEventListener("click", this.handleResultsClick);

    /**
     * ブラウザの戻る / 進む
     */
    window.addEventListener("popstate", this.handlePopState);
  }

  /**
   * ターム切り替え
   */
  handleTermClick(event) {
    const button = event.currentTarget;
    const term = button.dataset.term || "";

    if (this.isLoading) return;

    /**
     * 同じタームの1ページ目なら何もしない
     */
    if (term === this.currentTerm && this.currentPage === 1) {
      return;
    }

    this.fetchNews(term, 1, {
      updateHistory: true,
      scrollToArchive: false,
    });
  }

  /**
   * WP-PageNaviクリック
   */
  handleResultsClick(event) {
    const link = event.target.closest(".wp-pagenavi a");

    if (!link) return;

    event.preventDefault();

    if (this.isLoading) return;

    const page = this.getPageFromLink(link);

    if (!page) return;

    this.fetchNews(this.currentTerm, page, {
      updateHistory: true,
      scrollToArchive: true,
    });
  }

  /**
   * WP-PageNaviから移動先ページ番号を取得
   */
  getPageFromLink(link) {
    /**
     * PREV
     */
    if (link.classList.contains("previouspostslink")) {
      return Math.max(1, this.currentPage - 1);
    }

    /**
     * NEXT
     */
    if (link.classList.contains("nextpostslink")) {
      return this.currentPage + 1;
    }

    /**
     * 通常の数字リンク
     */
    const textPage = Number.parseInt(link.textContent.trim(), 10);

    if (Number.isInteger(textPage) && textPage > 0) {
      return textPage;
    }

    /**
     * 念のためhrefからも取得
     */
    const href = link.getAttribute("href") || "";

    const pathMatch = href.match(/\/page\/(\d+)\/?/);

    if (pathMatch) {
      return Number(pathMatch[1]);
    }

    const queryMatch = href.match(/[?&](?:paged|news_page)=(\d+)/);

    if (queryMatch) {
      return Number(queryMatch[1]);
    }

    return null;
  }

  /**
   * AJAX取得
   */
  async fetchNews(term, page, options = {}) {
    if (this.isLoading) return;

    const { updateHistory = false, scrollToArchive = false } = options;

    this.setLoading(true);

    const params = new URLSearchParams();

    params.set("action", "mediabirth_news_filter");

    params.set("nonce", this.nonce);

    params.set("term", term);

    params.set("paged", String(page));

    try {
      const response = await fetch(this.ajaxUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: params.toString(),
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const json = await response.json();

      if (!json.success || !json.data?.html) {
        throw new Error("NEWSの取得に失敗しました。");
      }

      /**
       * 差し替え前の古い記事に対する
       * IntersectionObserver監視を解除
       */
      if (this.inviewObserver) {
        this.inviewObserver.disconnect();
      }

      /**
       * 記事一覧 + PageNaviを差し替え
       */
      this.results.innerHTML = json.data.html;

      this.currentTerm = term;
      this.currentPage = page;

      this.root.dataset.currentTerm = term;

      this.root.dataset.currentPage = String(page);

      this.updateActiveTerm(term);

      /**
       * DOM差し替え後
       */
      requestAnimationFrame(() => {
        /**
         * 新しい.js-inviewを再監視
         */
        this.observeInviewItems();

        /**
         * ページャー操作時のみ
         * NEWS一覧上部へスムーススクロール
         */
        if (scrollToArchive) {
          this.scrollToArchive();
        }
      });

      if (updateHistory) {
        this.updateUrl(term, page);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * NEWS一覧上部へスクロール
   */
  scrollToArchive() {
    if (!this.scrollTarget) return;

    /**
     * OS側で「視差効果を減らす」が有効な場合は
     * スムーススクロールを使用しない
     */
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    this.scrollTarget.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  }

  /**
   * NEWS一覧用IntersectionObserverを生成
   */
  createInviewObserver() {
    /**
     * IntersectionObserver非対応環境では
     * そのまま表示状態にする
     */
    if (!("IntersectionObserver" in window)) {
      return;
    }

    this.inviewObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add("--inview-acv");

          /**
           * 一度表示された要素は監視終了
           */
          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: "0px 0px -10% 0px",
        threshold: 0,
      },
    );
  }

  /**
   * 現在の記事一覧内にある
   * .js-inviewを監視対象へ登録
   */
  observeInviewItems() {
    const items = Array.from(
      this.results.querySelectorAll(".js-inview:not(.--inview-acv)"),
    );

    if (!items.length) return;

    /**
     * IntersectionObserver非対応時
     */
    if (!this.inviewObserver) {
      items.forEach((item) => {
        item.classList.add("--inview-acv");
      });

      return;
    }

    items.forEach((item) => {
      this.inviewObserver.observe(item);
    });
  }

  /**
   * 選択中ターム表示
   */
  updateActiveTerm(term) {
    this.termButtons.forEach((button) => {
      const isActive = (button.dataset.term || "") === term;

      button.classList.toggle("--active", isActive);

      button.setAttribute("aria-selected", String(isActive));
    });
  }

  /**
   * URLだけ更新
   *
   * ページ遷移は発生しない。
   *
   * 例：
   * /news/?news_category=notice&news_page=2
   */
  updateUrl(term, page) {
    const url = new URL(this.archiveUrl, window.location.origin);

    if (term) {
      url.searchParams.set("news_category", term);
    }

    if (page > 1) {
      url.searchParams.set("news_page", String(page));
    }

    window.history.pushState(
      {
        newsTerm: term,
        newsPage: page,
      },
      "",
      url,
    );
  }

  /**
   * ブラウザ「戻る / 進む」
   */
  handlePopState() {
    const url = new URL(window.location.href);

    const term = url.searchParams.get("news_category") || "";

    const page = Math.max(1, Number(url.searchParams.get("news_page") || 1));

    this.fetchNews(term, page, {
      updateHistory: false,
      scrollToArchive: false,
    });
  }

  /**
   * ローディング状態
   */
  setLoading(isLoading) {
    this.isLoading = isLoading;

    this.root.classList.toggle("--loading", isLoading);

    this.results.setAttribute("aria-busy", String(isLoading));
  }

  /**
   * イベント解除
   */
  destroy() {
    this.termButtons.forEach((button) => {
      button.removeEventListener("click", this.handleTermClick);
    });

    this.results?.removeEventListener("click", this.handleResultsClick);

    window.removeEventListener("popstate", this.handlePopState);

    if (this.inviewObserver) {
      this.inviewObserver.disconnect();
      this.inviewObserver = null;
    }
  }
}

export default NewsFilter;
